// Vade Takibi → Detay: bir borcun özeti ve o borca yapılmış tahsilatların geçmişi.
import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Alert, Spinner, Badge } from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D";

const RECEIPT_TYPE_LABELS = {
    CASH: "Nakit",
    CREDIT_CARD: "Kredi Kartı",
    BANK_TRANSFER: "Havale/EFT",
    CHECK: "Çek",
    PROMISSORY_NOTE: "Senet",
};

// Tutarlar hareketin kendi para biriminde gösterilir: ₺2.000,00 / $2.000,00 / €2.000,00
const formatAmount = (amount, currency) => {
    if (amount == null) return "-";
    const num = Number(amount);
    const code = (currency || "TRY").toUpperCase();
    try {
        return num.toLocaleString("tr-TR", { style: "currency", currency: code });
    } catch {
        return `${num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
    }
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-");

const StatusBadge = ({ status, overdue }) => {
    if (status === "CLOSED") return <Badge color="success" className="rounded-pill px-3 py-2">Ödendi</Badge>;
    if (status === "CANCELED") return <Badge color="dark" className="rounded-pill px-3 py-2">İptal</Badge>;
    if (status === "PARTIALLY_PAID")
        return <Badge className="rounded-pill px-3 py-2" style={{ backgroundColor: "#0dcaf0" }}>Kısmen Ödendi</Badge>;
    if (overdue) return <Badge color="danger" className="rounded-pill px-3 py-2">Vadesi Geçmiş</Badge>;
    return <Badge className="rounded-pill px-3 py-2" style={{ backgroundColor: "#fd7e14" }}>Bekleyen</Badge>;
};

const KV = ({ label, children }) => (
    <tr>
        <th style={{ width: "45%", fontWeight: 500, color: "#6c757d" }}>{label}</th>
        <td>{children ?? "-"}</td>
    </tr>
);

const SummaryCard = ({ label, value, color }) => (
    <Card className="rounded-4 border-0 shadow-sm h-100">
        <CardBody className="py-3">
            <div className="text-muted" style={{ fontSize: ".8rem" }}>{label}</div>
            <div className="fw-bold mt-1" style={{ fontSize: "1.15rem", color }}>{value}</div>
        </CardBody>
    </Card>
);

/** Tahsilat tipine göre anlamlı referans bilgisi; yoksa açıklamaya düşer. */
const referenceOf = (p) => {
    if (p.checkNumber) return `Çek No: ${p.checkNumber}`;
    if (p.promissoryNoteNumber) return `Senet No: ${p.promissoryNoteNumber}`;
    if (p.bankName) return `Banka: ${p.bankName}`;
    return p.generalDescription || "—";
};

export default function DebtPaymentDetailPage() {
    const { debtId } = useParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isAccountRole, setIsAccountRole] = useState(false);

    useEffect(() => {
        document.title = "Borç Ödeme Detayı | Tahsilat360";
        axios.get(API.auth_me)
            .then((res) => {
                const roles = res.data?.data?.roles || [];
                setIsAccountRole(roles.some((r) => r.startsWith("ROLE_ACCOUNT_")));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        let alive = true;
        const run = async () => {
            try {
                setLoading(true);
                setError("");
                const res = await axios.get(API.debt_payment_detail(debtId));
                if (alive) setData(res.data?.data || null);
            } catch (e) {
                if (alive) setError(e?.response?.data?.message || "Borç ödeme detayı alınamadı.");
            } finally {
                if (alive) setLoading(false);
            }
        };
        if (debtId) run();
        return () => { alive = false; };
    }, [debtId]);

    if (loading) {
        return (
            <div className="page-content">
                <div className="container-fluid text-center p-5">
                    <Spinner color="primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title={isAccountRole ? "Borç Takibi" : "Vade Takibi"} breadcrumbItem="Borç Ödeme Detayı" />

                <div className="d-flex mb-3">
                    <BackButton />
                </div>

                {error && <Alert color="danger" className="rounded-4">{error}</Alert>}

                {data && (
                    <>
                        {/* Özet tutarlar */}
                        <Row className="g-3 mb-3">
                            <Col md={3} xs={6}>
                                <SummaryCard label="Toplam Borç" value={formatAmount(data.totalDebt, data.currency)} color="#dc3545" />
                            </Col>
                            <Col md={3} xs={6}>
                                <SummaryCard label="Toplam Tahsilat" value={formatAmount(data.totalPaid, data.currency)} color="#198754" />
                            </Col>
                            <Col md={3} xs={6}>
                                <SummaryCard label="Kalan Borç" value={formatAmount(data.remaining, data.currency)} color={NAVY} />
                            </Col>
                            <Col md={3} xs={6}>
                                <Card className="rounded-4 border-0 shadow-sm h-100">
                                    <CardBody className="py-3">
                                        <div className="text-muted" style={{ fontSize: ".8rem" }}>Durum</div>
                                        <div className="mt-2">
                                            <StatusBadge status={data.invoiceStatus} overdue={data.overdue} />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="g-3">
                            {/* Borç bilgisi */}
                            <Col lg={4}>
                                <Card className="rounded-4 border-0 shadow-sm h-100">
                                    <CardHeader className="bg-white border-0 rounded-top-4">
                                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "1rem" }}>
                                            <i className="bx bx-file me-2" style={{ color: NAVY }} />
                                            Borç Bilgisi
                                        </h5>
                                    </CardHeader>
                                    <CardBody className="pt-2">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <KV label="Cari">{data.accountName}</KV>
                                                <KV label="Belge No">{data.documentNumber || "—"}</KV>
                                                <KV label="Hareket Tarihi">{formatDate(data.movementDate)}</KV>
                                                <KV label="Vade Tarihi">{formatDate(data.dueDate)}</KV>
                                                {/* Borç kapandıysa ne zaman ödendiği burada net görünsün;
                                                    kullanıcı kısmi ödeme tablosundan tarih aramak zorunda kalmasın. */}
                                                {data.invoiceStatus === "CLOSED" && (
                                                    <KV label="Ödenme Tarihi">
                                                        {data.paidOffDate ? (
                                                            <span className="fw-semibold text-success">
                                                                {formatDate(data.paidOffDate)}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="text-muted"
                                                                title="Borç kapalı görünüyor ancak eşleşmiş tahsilat kaydı yok — borç elle kapatılmış ya da tahsilat–borç eşleştirmesi eklenmeden önce girilmiş olabilir."
                                                            >
                                                                —
                                                            </span>
                                                        )}
                                                    </KV>
                                                )}
                                                <KV label="Para Birimi">{data.currency}</KV>
                                                <KV label="Açıklama">{data.generalDescription || "—"}</KV>
                                            </tbody>
                                        </table>
                                    </CardBody>
                                </Card>
                            </Col>

                            {/* Kısmi ödeme geçmişi */}
                            <Col lg={8}>
                                <Card className="rounded-4 border-0 shadow-sm h-100">
                                    <CardHeader className="bg-white border-0 rounded-top-4">
                                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "1rem" }}>
                                            <i className="bx bx-list-ul me-2" style={{ color: NAVY }} />
                                            Ödeme Geçmişi
                                            <span className="text-muted fw-normal ms-2" style={{ fontSize: ".85rem" }}>
                                                ({data.payments?.length || 0} tahsilat)
                                            </span>
                                        </h5>
                                    </CardHeader>
                                    <CardBody className="pt-2">
                                        {data.payments?.length > 0 ? (
                                            <div className="table-responsive rounded-4 border">
                                                <Table className="table table-hover align-middle mb-0">
                                                    <Thead>
                                                        <Tr>
                                                            <Th className="ps-3">Tarih</Th>
                                                            <Th>Ödeme Tutarı</Th>
                                                            <Th>Bu Borca Düşen</Th>
                                                            <Th>Kalan Borç</Th>
                                                            <Th>Ödeme Tipi</Th>
                                                            <Th>İşlemi Yapan</Th>
                                                            <Th className="pe-3">Referans</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        <Tr className="table-light">
                                                            <Td className="ps-3">{formatDate(data.movementDate)}</Td>
                                                            <Td className="text-muted">Borç kaydı</Td>
                                                            <Td>—</Td>
                                                            <Td className="fw-semibold" style={{ color: "#dc3545" }}>
                                                                {formatAmount(data.totalDebt, data.currency)}
                                                            </Td>
                                                            <Td>—</Td>
                                                            <Td>—</Td>
                                                            <Td className="pe-3">—</Td>
                                                        </Tr>
                                                        {data.payments.map((p) => (
                                                            <Tr key={p.id}>
                                                                <Td className="ps-3">{formatDate(p.movementDate)}</Td>
                                                                <Td>
                                                                    <span className="fw-semibold text-success">
                                                                        {formatAmount(p.amount, p.currency)}
                                                                    </span>
                                                                    {p.currency !== data.currency && p.amountTry != null && (
                                                                        <small className="text-muted d-block">
                                                                            ≈ {formatAmount(p.amountTry, "TRY")}
                                                                        </small>
                                                                    )}
                                                                </Td>
                                                                <Td className="fw-semibold">{formatAmount(p.appliedAmount, data.currency)}</Td>
                                                                <Td className="fw-semibold" style={{ color: Number(p.remainingAfter) > 0 ? "#dc3545" : "#198754" }}>
                                                                    {formatAmount(p.remainingAfter, data.currency)}
                                                                </Td>
                                                                <Td>{RECEIPT_TYPE_LABELS[p.receiptType] || p.receiptType || "—"}</Td>
                                                                <Td>{p.createdByUsername || <span className="text-muted">Otomatik</span>}</Td>
                                                                <Td className="pe-3 text-muted small">{referenceOf(p)}</Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted p-4">
                                                <i className="bx bx-receipt display-4 d-block mb-2" />
                                                Bu borca henüz tahsilat düşmedi.
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </div>
    );
}
